import React, {useEffect, useState} from 'react'
import Axios from 'axios'
import {useParams, Link} from 'react-router-dom'

const CARD_WIDTH = 180
const CARD_HEIGHT = Math.round((CARD_WIDTH * 680) / 488)
const GRID_GAP = 12 // Match basics page

// Color order for grouping
const COLOR_ORDER = ['White', 'Blue', 'Black', 'Red', 'Green', 'Multicolor', 'Colorless', 'Land', 'Other']

// Detect if we're running locally or on production
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

// Helper function to get backend URL with fallback
const getBackendUrl = () => {
  if (!isLocalDev) return 'https://moxtest.app'
  return `http://localhost:${__DEV_BACKEND_PORT__}`
}

function getColorGroup(card) {
  if (card.type_line && card.type_line.includes('Land')) return 'Land'
  if (!card.colors || card.colors.length === 0) return 'Colorless'
  if (card.colors.length > 1) return 'Multicolor'
  switch (card.colors[0]) {
    case 'W':
      return 'White'
    case 'U':
      return 'Blue'
    case 'B':
      return 'Black'
    case 'R':
      return 'Red'
    case 'G':
      return 'Green'
    default:
      return 'Other'
  }
}

function groupAndSortCards(cards) {
  console.log(`[SetDetails] 🔄 Grouping and sorting ${cards.length} cards`)

  const groups = {}
  for (const card of cards) {
    const group = getColorGroup(card)
    if (!groups[group]) groups[group] = []
    groups[group].push(card)
  }

  console.log(`[SetDetails] 📊 Groups found:`, Object.keys(groups))

  // Sort cards within each group by CMC, then by collector number
  for (const group in groups) {
    console.log(`[SetDetails] 🔄 Sorting ${group} group (${groups[group].length} cards)`)
    groups[group].sort((a, b) => {
      const aCmc = parseFloat(a.cmc) || 0
      const bCmc = parseFloat(b.cmc) || 0
      if (aCmc !== bCmc) {
        return aCmc - bCmc
      }
      // If CMC is the same, sort by collector number
      const aNum = parseInt(a.collector_number) || 0
      const bNum = parseInt(b.collector_number) || 0
      return aNum - bNum
    })

    // Debug: Log first few cards in each group
    if (groups[group].length > 0) {
      console.log(
        `[SetDetails] 📋 ${group} group sample:`,
        groups[group].slice(0, 3).map(card => ({
          name: card.name,
          cmc: card.cmc,
          collector_number: card.collector_number,
        })),
      )
    }
  }

  // Return sorted groups
  const result = COLOR_ORDER.filter(group => groups[group]).map(group => ({group, cards: groups[group]}))
  console.log(
    `[SetDetails] ✅ Final grouped result:`,
    result.map(r => ({group: r.group, count: r.cards.length})),
  )
  return result
}

function groupAlternateArts(cards) {
  const alternates = []
  const alternateGroups = {}

  for (const card of cards) {
    if (
      card.full_art ||
      (card.frame_effects && card.frame_effects.includes('showcase')) ||
      (card.promo_types && card.promo_types.length > 0)
    ) {
      let style = 'Alternate Art'
      if (card.full_art) style = 'Full Art'
      if (card.frame_effects && card.frame_effects.includes('showcase')) style = 'Showcase'
      if (card.promo_types && card.promo_types.includes('prerelease')) style = 'Prerelease'
      if (card.promo_types && card.promo_types.includes('promopack')) style = 'Promo Pack'

      if (!alternateGroups[style]) alternateGroups[style] = []
      alternateGroups[style].push(card)
    }
  }

  for (const [style, cards] of Object.entries(alternateGroups)) {
    alternates.push({style, grouped: groupAndSortCards(cards)})
  }

  return alternates
}

function SetDetails() {
  const {setName} = useParams()
  const [cards, setCards] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [setCode, setSetCode] = useState(null)
  const [displaySetName, setDisplaySetName] = useState(null)
  const [loadingProgress, setLoadingProgress] = useState('Loading...')

  useEffect(() => {
    async function fetchSetCards() {
      setLoading(true)
      setError(null)
      setCards([])
      setLoadingProgress('Looking up set code...')

      try {
        // Step 1: The URL now contains the set code directly
        const setCode = decodeURIComponent(setName)
        console.log(`[SetDetails] 🔍 Processing set code: ${setCode}`)

        setSetCode(setCode)
        console.log(`[SetDetails] ✅ Using set code: ${setCode}`)

        // Step 2: Use our optimized endpoint to get cards immediately
        setLoadingProgress('Loading available cards...')
        const backendUrl = getBackendUrl()
        console.log(`[SetDetails] 📡 Fetching cards from optimized endpoint: ${backendUrl}/cards/fullset/${setCode}`)

        const fullsetRes = await Axios.get(`${backendUrl}/cards/fullset/${setCode}`)
        console.log(`[SetDetails] 📋 Got immediate response: ${fullsetRes.data.totalFound} cards`)

        if (fullsetRes.data.cards && fullsetRes.data.cards.length > 0) {
          // Use the full card data that's now being returned from backend
          const convertedCards = fullsetRes.data.cards.map(card => ({
            id: card.id || card.filename,
            name: card.name,
            image_uris: card.image_uris || {normal: card.url},
            setCode: card.setCode,
            source: card.source,
            // Use the full card data for proper filtering and grouping
            type_line: card.type_line || 'Unknown',
            colors: card.colors || [],
            cmc: card.cmc || 0,
            collector_number: card.collector_number || '0',
            mana_cost: card.mana_cost || '',
            full_art: card.full_art || false,
            frame_effects: card.frame_effects || [],
            promo_types: card.promo_types || [],
            card_faces: card.card_faces || null,
          }))

          setCards(convertedCards)
          console.log(`[SetDetails] ✅ Set cards immediately: ${convertedCards.length} cards`)

          // Debug: Log first few cards to check their data structure
          if (convertedCards.length > 0) {
            console.log(`[SetDetails] 🔍 Sample card data:`, {
              name: convertedCards[0].name,
              colors: convertedCards[0].colors,
              cmc: convertedCards[0].cmc,
              type_line: convertedCards[0].type_line,
              collector_number: convertedCards[0].collector_number,
            })
          }

          // Extract set name from the first card if available
          if (convertedCards.length > 0 && convertedCards[0].set_name) {
            setDisplaySetName(convertedCards[0].set_name)
          }

          // If we have a good number of cards, we can show the page
          if (fullsetRes.data.totalFound > 10) {
            setLoading(false)
            setLoadingProgress('Cards loaded! Checking for more in background...')
          } else {
            // If we have very few cards, wait a bit for the async process
            setLoadingProgress('Loading more cards...')
            setTimeout(() => {
              setLoading(false)
              setLoadingProgress('Cards loaded!')
            }, 2000)
          }
        } else {
          // No cards found, wait for async process
          setLoadingProgress('No cards found yet, loading from Scryfall...')
          setTimeout(() => {
            setLoading(false)
            setLoadingProgress('Cards loaded!')
          }, 3000)
        }
      } catch (e) {
        console.error(`[SetDetails] 💥 Error loading set:`, e)
        setError('Failed to load cards for this set.')
        setLoading(false)
      }
    }
    fetchSetCards()
  }, [setName])

  // Separate main printings from alternates
  const mainPrintings = cards.filter(
    card =>
      !card.full_art &&
      !(card.frame_effects && card.frame_effects.includes('showcase')) &&
      (!card.promo_types || card.promo_types.length === 0),
  )
  const alternates = groupAlternateArts(cards)
  const grouped = groupAndSortCards(mainPrintings)

  return (
    <div
      style={{background: '#222', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0}}>
      <div style={{width: '100%', maxWidth: 1200, marginBottom: 24, flexShrink: 0}}>
        <Link to="/" style={{color: '#aaa', textDecoration: 'underline', fontSize: 16}}>
          &larr; Back to Basics
        </Link>
        {displaySetName && (
          <h2 style={{color: '#fff', margin: '16px 0 0 0', fontWeight: 400, fontSize: 28}}>{displaySetName}</h2>
        )}
        {setCode && <div style={{color: '#666', fontSize: 14, marginTop: 4}}>Set Code: {setCode}</div>}
      </div>
      {loading ? (
        <div style={{color: '#fff', fontSize: 24}}>{loadingProgress}</div>
      ) : error ? (
        <div style={{color: 'red', fontSize: 18}}>{error}</div>
      ) : cards.length === 0 ? (
        <div style={{color: '#fff', fontSize: 18}}>No cards found for this set.</div>
      ) : (
        <div
          className="set-scroll-container"
          style={{width: '100%', maxWidth: 1200, flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 32}}>
          {/* Main printings */}
          {grouped.map(({group, cards}) => (
            <div key={group} style={{marginBottom: 32}}>
              <div
                style={{
                  color: '#fffa',
                  fontSize: 20,
                  fontWeight: 500,
                  margin: '16px 0 8px 0',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}>
                {group}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fit, minmax(${CARD_WIDTH}px, 1fr))`,
                  gap: GRID_GAP,
                  justifyContent: 'center',
                }}>
                {cards.map(card => (
                  <div key={card.id} style={{}}>
                    <img
                      src={card.image_uris?.normal}
                      alt={card.name}
                      style={{
                        width: CARD_WIDTH,
                        height: CARD_HEIGHT,
                        display: 'block',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        background: '#111',
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {/* Alternate arts */}
          {alternates.map(({style, grouped}) => (
            <div key={style} style={{marginBottom: 32}}>
              <div
                style={{
                  color: '#fffa',
                  fontSize: 20,
                  fontWeight: 500,
                  margin: '16px 0 8px 0',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}>
                {style}
              </div>
              {grouped.map(({group, cards}) => (
                <div key={group} style={{marginBottom: 16}}>
                  <div
                    style={{
                      color: '#aaa',
                      fontSize: 16,
                      fontWeight: 500,
                      margin: '8px 0 4px 0',
                    }}>
                    {group}
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(auto-fit, minmax(${CARD_WIDTH}px, 1fr))`,
                      gap: GRID_GAP,
                      justifyContent: 'center',
                    }}>
                    {cards.map(card => (
                      <div key={card.id} style={{}}>
                        <img
                          src={card.image_uris?.normal}
                          alt={card.name}
                          style={{
                            width: CARD_WIDTH,
                            height: CARD_HEIGHT,
                            display: 'block',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            background: '#111',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default SetDetails
